import { useEffect, useRef, useState } from 'react'

import { useOrderDetails } from 'src/contexts/OrderDetailsContext'
import { useUserContext } from 'src/contexts/UserProvider'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useIsTranscriptAvailable, useIsTranscriptEmpty, useTranscriptConnectionState } from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptError } from 'src/contexts/Transcript/TranscriptErrorContext'
import { isPdfDisplayMode } from 'src/utils/env'

/**
 * Small debounce before showing the global loading overlay.
 * Prevents flash/flicker when booting/hydration resolves almost immediately.
 */
const GLOBAL_OVERLAY_SHOW_DELAY_MS = 120
/**
 * Once shown, keep the overlay visible for a minimum duration.
 * Prevents rapid blink-on/blink-off jitter during quick state transitions.
 */
const GLOBAL_OVERLAY_MIN_VISIBLE_MS = 400

export const usePrerequisites = () => {
    const { isUserFetched } = useUserContext()
    const isTranscriptAvailable = useIsTranscriptAvailable()
    const isTranscriptEmpty = useIsTranscriptEmpty()
    const { transcriptError } = useTranscriptError()
    const transcriptMetadata = useTranscriptMetadata()

    // In PDF display mode, we don't need to wait for fonts to load
    const [isInitialFontAvailable, setIsInitialFontAvailable] = useState(isPdfDisplayMode())
    const hasTranscriptMetadata = Boolean(transcriptMetadata)
    const isReadyToDisplayViewerContainer = hasTranscriptMetadata && !transcriptError
    const isDocumentViewMode = transcriptMetadata?.viewMode === 'document'
    const isDocumentCompleted = isDocumentViewMode && Boolean(transcriptMetadata?.sessionFinishedAt)
    const isDocumentHydrating = isDocumentCompleted && isTranscriptEmpty
    const isReadyToDisplayTranscript = isTranscriptAvailable && hasTranscriptMetadata && !isDocumentHydrating

    const [isPrerequisitesLoading, setIsPrerequisitesLoading] = useState(
        (!isReadyToDisplayTranscript || (!isInitialFontAvailable && !isTranscriptEmpty)) && !transcriptError,
    )
    useEffect(() => {
        // show loader if:
        // 1) there's no error, AND
        // 2) either:
        //    - we're not ready to display transcript (request not completed / no metadata / no language), OR
        //    - initial font is not available and transcript isn't empty (when the transcript IS empty,
        //      the font won't be loaded, so there's no need to wait for it)
        setIsPrerequisitesLoading((!isReadyToDisplayTranscript || (!isInitialFontAvailable && !isTranscriptEmpty)) && !transcriptError)
    }, [isInitialFontAvailable, isReadyToDisplayTranscript, isTranscriptEmpty, transcriptError])

    const { isInitialized, isViewable } = useTranscriptConnectionState()
    // DocumentTranscriptClient keeps isInitialized false while polling and on every failed getTranscript (it retries).
    // Waiting on that flag would leave the global overlay up forever on transcript fetch errors — see DocumentTranscriptClient._pollForTranscript catch block.

    // Track whether we've ever reached `isInitialized: true`. Once we have, subsequent
    // `!isInitialized` transitions (e.g. language switch, client swap) are re-initializations,
    // not fresh boots — we prefer a panel-local loader over the full-viewport overlay
    // to avoid covering the whole chrome on every language change.
    const hasEverBeenInitializedRef = useRef(isInitialized)
    if (isInitialized) hasEverBeenInitializedRef.current = true
    const isReinitializing = hasEverBeenInitializedRef.current && !isInitialized

    // Cold-boot rule: hold the global overlay only while we have nothing coherent to
    // render. A cache hydrate hit sets `isViewable` and gives us a complete-enough
    // baseline; a non-empty doc without that signal means a live message arrived
    // pre-sync (partial paint), which should still keep the overlay up until /sync
    // fills in the rest.
    const shouldDisplayLoader =
        !transcriptError && (isPrerequisitesLoading || (!isDocumentViewMode && !isInitialized && !isReinitializing && !isViewable))

    // Panel-local loader rendered in place of the transcript viewer while we reconnect /
    // swap clients after the initial load. Intentionally excluded from `shouldRequestGlobalOverlay`.
    const shouldDisplayTranscriptLoader = !transcriptError && !isDocumentViewMode && isReinitializing

    const shouldDisplayDocumentHydrationOverlay = !transcriptError && isDocumentHydrating

    const { isOrderPending } = useOrderDetails()
    const shouldWaitForOrder = isTranscriptEmpty && isOrderPending

    /** Full-viewport overlay: waiting for metadata, user identify (/users/whoami), order details, or transcript prerequisites / connection not ready. */
    const shouldRequestGlobalOverlay =
        !transcriptError &&
        (!hasTranscriptMetadata || !isUserFetched || shouldDisplayLoader || shouldDisplayDocumentHydrationOverlay || shouldWaitForOrder)

    const [isGlobalOverlayVisible, setIsGlobalOverlayVisible] = useState(shouldRequestGlobalOverlay)
    const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const visibleSinceRef = useRef<number>(shouldRequestGlobalOverlay ? Date.now() : 0)

    useEffect(() => {
        const clearShowTimer = () => {
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current)
                showTimerRef.current = null
            }
        }
        const clearHideTimer = () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current)
                hideTimerRef.current = null
            }
        }

        if (shouldRequestGlobalOverlay) {
            clearHideTimer()

            if (isGlobalOverlayVisible) {
                return () => {
                    clearShowTimer()
                    clearHideTimer()
                }
            }

            clearShowTimer()
            showTimerRef.current = setTimeout(() => {
                visibleSinceRef.current = Date.now()
                setIsGlobalOverlayVisible(true)
            }, GLOBAL_OVERLAY_SHOW_DELAY_MS)

            return () => {
                clearShowTimer()
                clearHideTimer()
            }
        }

        clearShowTimer()

        if (!isGlobalOverlayVisible) {
            return () => {
                clearShowTimer()
                clearHideTimer()
            }
        }

        const elapsedVisibleMs = Math.max(0, Date.now() - visibleSinceRef.current)
        const hideDelayMs = Math.max(0, GLOBAL_OVERLAY_MIN_VISIBLE_MS - elapsedVisibleMs)
        hideTimerRef.current = setTimeout(() => {
            setIsGlobalOverlayVisible(false)
        }, hideDelayMs)

        return () => {
            clearShowTimer()
            clearHideTimer()
        }
    }, [isGlobalOverlayVisible, shouldRequestGlobalOverlay])

    return {
        isInitialFontAvailable,
        isReadyToDisplayTranscript,
        isReadyToDisplayViewerContainer,
        shouldDisplayGlobalOverlay: isGlobalOverlayVisible,
        shouldDisplayTranscriptLoader,
        setIsInitialFontAvailable,
    }
}
